import { assertCryptoPolicy, assertPlatformPolicy } from "./validation.js";
function intersectAlgorithms(enterprisePolicy, platformPolicy) {
    return enterprisePolicy.signatures.allowed.filter((algorithm) => !enterprisePolicy.signatures.forbidden.includes(algorithm) &&
        (!platformPolicy || (platformPolicy.signatures.allowed.includes(algorithm) && !platformPolicy.signatures.forbidden.includes(algorithm))));
}
export function resolveEffectivePolicyConstraints(enterprisePolicy, platformPolicy, profileID) {
    const profile = profileID ? enterprisePolicy.workload_profiles?.[profileID] : undefined;
    const enterpriseAlgorithms = profile?.allowed_algorithms ?? enterprisePolicy.signatures.allowed;
    const enterpriseDeployments = profile?.allowed_deployment_modes ?? platformPolicy.allowed_deployment_modes;
    return {
        allowed_algorithms: enterpriseAlgorithms.filter((algorithm) => enterprisePolicy.signatures.allowed.includes(algorithm) &&
            !enterprisePolicy.signatures.forbidden.includes(algorithm) &&
            platformPolicy.signatures.allowed.includes(algorithm) &&
            !platformPolicy.signatures.forbidden.includes(algorithm)),
        allowed_key_establishment: enterprisePolicy.key_establishment.allowed.filter((algorithm) => platformPolicy.key_establishment.allowed.includes(algorithm)),
        allowed_deployment_modes: enterpriseDeployments.filter((mode) => platformPolicy.allowed_deployment_modes.includes(mode)),
        max_validity_seconds: Math.min(enterprisePolicy.validity.max_seconds, platformPolicy.validity.max_seconds, profile?.max_validity_seconds ?? Number.MAX_SAFE_INTEGER),
        offline_renewal_max_seconds: Math.min(enterprisePolicy.validity.offline_renewal_max_seconds, platformPolicy.validity.offline_renewal_max_seconds),
        minimum_customer_approvals: Math.max(enterprisePolicy.customer_approval_quorum?.required ?? 1, platformPolicy.minimum_customer_approvals),
        require_capability_evidence: Boolean(platformPolicy.require_capability_evidence || profile?.require_capability_evidence),
    };
}
export function assertPolicySatisfiableUnderPlatform(enterprisePolicy, platformPolicy, now = new Date()) {
    assertCryptoPolicy(enterprisePolicy, now);
    assertPlatformPolicy(platformPolicy, now);
    const enterprise = resolveEffectivePolicyConstraints(enterprisePolicy, platformPolicy);
    if (enterprise.allowed_algorithms.length === 0)
        throw new TypeError("enterprise policy has no signature algorithm permitted by Sectigo platform policy");
    if (enterprise.allowed_key_establishment.length === 0)
        throw new TypeError("enterprise policy has no key establishment permitted by Sectigo platform policy");
    for (const profileID of Object.keys(enterprisePolicy.workload_profiles)) {
        const effective = resolveEffectivePolicyConstraints(enterprisePolicy, platformPolicy, profileID);
        if (effective.allowed_algorithms.length === 0)
            throw new TypeError(`${profileID} has no signature algorithm permitted by the complete policy stack`);
        if (effective.allowed_deployment_modes.length === 0)
            throw new TypeError(`${profileID} has no deployment path permitted by the complete policy stack`);
    }
}
function spiffePrefixMatches(identity, prefixes) {
    return prefixes.some((prefix) => prefix.endsWith("/") && identity.startsWith(prefix));
}
function dnsSuffixMatches(name, suffixes) {
    const normalized = name.toLowerCase().replace(/\.$/, "");
    const withoutWildcard = normalized.replace(/^\*\./, "");
    return suffixes.some((suffix) => {
        const base = suffix.toLowerCase().replace(/^\./, "").replace(/\.$/, "");
        return base.length > 0 && (withoutWildcard === base || withoutWildcard.endsWith(`.${base}`));
    });
}
export function evaluatePolicy(transaction, enterprisePolicy, context) {
    const reasons = [];
    const now = context.now.getTime();
    const profile = enterprisePolicy.workload_profiles?.[transaction.request.profile_id];
    if (!/^[A-Za-z0-9_-]{43}$/.test(context.policySha256)) {
        reasons.push("enterprise policy content digest is invalid");
    }
    for (const policy of [enterprisePolicy, ...(context.platformPolicy ? [context.platformPolicy] : [])]) {
        if (policy.status !== "active")
            reasons.push(`${policy.id} is not active`);
        if (now < Date.parse(policy.not_before))
            reasons.push(`${policy.id} is not active yet`);
        if (now >= Date.parse(policy.expires_at))
            reasons.push(`${policy.id} is expired`);
    }
    if (transaction.policy_version !== enterprisePolicy.version) {
        reasons.push(`transaction policy version ${transaction.policy_version} is stale`);
    }
    if (context.platformPolicy && !/^[A-Za-z0-9_-]{43}$/.test(context.platformPolicySha256 ?? "")) {
        reasons.push("Sectigo platform policy content digest is invalid");
    }
    const allowed = intersectAlgorithms(enterprisePolicy, context.platformPolicy);
    const requested = transaction.request.requested_algorithm;
    if (!allowed.includes(requested))
        reasons.push(`${requested} is not in the effective allow-list`);
    if (!profile || profile.id !== transaction.request.profile_id) {
        reasons.push(`workload profile ${transaction.request.profile_id} is not active`);
    }
    else {
        if (!profile.allowed_algorithms.includes(requested)) {
            reasons.push(`${requested} is not allowed by workload profile ${profile.id}`);
        }
        if (!spiffePrefixMatches(transaction.requester, profile.allowed_spiffe_prefixes)) {
            reasons.push(`requester is outside workload profile ${profile.id}`);
        }
        for (const san of transaction.request.sans) {
            if (!dnsSuffixMatches(san, profile.allowed_dns_suffixes)) {
                reasons.push(`DNS SAN ${san} is outside workload profile ${profile.id}`);
            }
        }
        const deploymentMode = (transaction.request.deployment_mode ?? "single");
        if (!profile.allowed_deployment_modes.includes(deploymentMode)) {
            reasons.push(`deployment mode ${deploymentMode} is not allowed by workload profile ${profile.id}`);
        }
        if (context.platformPolicy && !context.platformPolicy.allowed_deployment_modes.includes(deploymentMode)) {
            reasons.push(`deployment mode ${deploymentMode} is forbidden by Sectigo platform policy ${context.platformPolicy.id}`);
        }
        if (profile.require_capability_evidence && !context.capability) {
            reasons.push(`workload profile ${profile.id} requires capability evidence`);
        }
        if (context.platformPolicy?.require_capability_evidence && !context.capability) {
            reasons.push(`Sectigo platform policy ${context.platformPolicy.id} requires capability evidence`);
        }
    }
    const maxValidity = Math.min(enterprisePolicy.validity.max_seconds, context.platformPolicy?.validity.max_seconds ?? Number.MAX_SAFE_INTEGER, profile?.max_validity_seconds ?? Number.MAX_SAFE_INTEGER);
    if (transaction.request.validity_seconds > maxValidity) {
        reasons.push(`validity exceeds effective maximum of ${maxValidity} seconds`);
    }
    if (context.capability) {
        if (Date.parse(context.capability.expires_at) <= now)
            reasons.push("capability evidence is expired");
        if (!context.capability.signature_algorithms.includes(requested)) {
            reasons.push(`endpoint capability does not include ${requested}`);
        }
    }
    return {
        decision: reasons.length === 0 ? "allow" : "deny",
        policy_id: enterprisePolicy.id,
        policy_version: enterprisePolicy.version,
        policy_sha256: context.policySha256,
        evaluated_at: context.now.toISOString(),
        reasons,
        ...(reasons.length === 0
            ? {
                effective_algorithm: requested,
                effective_validity_seconds: transaction.request.validity_seconds,
                effective_profile_id: transaction.request.profile_id,
            }
            : {}),
        ...(context.platformPolicy ? {
            platform_policy_id: context.platformPolicy.id,
            platform_policy_version: context.platformPolicy.version,
            platform_policy_sha256: context.platformPolicySha256,
        } : {}),
    };
}
//# sourceMappingURL=policy.js.map